import { TouchEvent, useCallback, useEffect, useRef } from 'react';

interface SwipeHandlers {
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
}

interface TouchPosition {
	x: number;
	y: number;
}

export const useSwipe = (handlers: SwipeHandlers, minSwipeDistance = 50) => {
	const touchStart = useRef<TouchPosition | null>(null);
	const touchEnd = useRef<TouchPosition | null>(null);
	const elementRef = useRef<HTMLElement | null>(null);
	const isDraggingRef = useRef(false);
	const translateXRef = useRef(0);
	const animationFrameRef = useRef<number | null>(null);
	const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const handlersRef = useRef(handlers);
	handlersRef.current = handlers;

	const applyTransform = useCallback((x: number, withTransition = false) => {
		translateXRef.current = x;
		if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current);
		animationFrameRef.current = requestAnimationFrame(() => {
			if (elementRef.current) {
				elementRef.current.style.transform = `translateX(${x}px)`;
				elementRef.current.style.transition = withTransition
					? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
					: 'none';
			}
			animationFrameRef.current = null;
		});
	}, []);

	const clearGesture = useCallback(() => {
		touchStart.current = null;
		touchEnd.current = null;
		isDraggingRef.current = false;
		translateXRef.current = 0;
		resetTimeoutRef.current = null;
		if (elementRef.current) elementRef.current.style.willChange = '';
	}, []);

	const resetImmediately = useCallback(() => {
		if (animationFrameRef.current !== null) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
		if (elementRef.current) {
			elementRef.current.style.transition = 'none';
			elementRef.current.style.transform = 'translateX(0px)';
		}
		clearGesture();
	}, [clearGesture]);

	const resetPosition = useCallback(() => {
		if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
		applyTransform(0, true);
		resetTimeoutRef.current = setTimeout(clearGesture, 300);
	}, [applyTransform, clearGesture]);

	const completeSwipe = useCallback(
		(direction: 'left' | 'right') => {
			if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
			const targetX = direction === 'left' ? -window.innerWidth : window.innerWidth;
			applyTransform(targetX, true);

			resetTimeoutRef.current = setTimeout(() => {
				if (direction === 'left') {
					handlersRef.current.onSwipeLeft?.();
				} else {
					handlersRef.current.onSwipeRight?.();
				}

				if (elementRef.current) {
					elementRef.current.style.transition = 'none';
					elementRef.current.style.transform = 'translateX(0px)';
				}
				clearGesture();
			}, 300);
		},
		[applyTransform, clearGesture],
	);

	const onTouchStart = useCallback((e: TouchEvent) => {
		if (resetTimeoutRef.current) {
			clearTimeout(resetTimeoutRef.current);
			resetTimeoutRef.current = null;
		}
		touchEnd.current = null;
		touchStart.current = {
			x: e.targetTouches[0].clientX,
			y: e.targetTouches[0].clientY,
		};
		isDraggingRef.current = true;

		if (e.currentTarget instanceof HTMLElement) {
			elementRef.current = e.currentTarget;
			elementRef.current.style.willChange = 'transform';
		}
	}, []);

	const onTouchMove = useCallback(
		(e: TouchEvent) => {
			if (!touchStart.current || !isDraggingRef.current) return;

			touchEnd.current = {
				x: e.targetTouches[0].clientX,
				y: e.targetTouches[0].clientY,
			};

			const distanceX = touchStart.current.x - touchEnd.current.x;
			const distanceY = touchStart.current.y - touchEnd.current.y;
			const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

			if (isHorizontalSwipe) {
				let newTranslateX = -distanceX;
				const maxTranslate = 150;
				const resistance = 0.4;

				if (Math.abs(newTranslateX) > maxTranslate) {
					const excess = Math.abs(newTranslateX) - maxTranslate;
					newTranslateX = newTranslateX > 0 ? maxTranslate + excess * resistance : -maxTranslate - excess * resistance;
				}

				applyTransform(newTranslateX);
			}
		},
		[applyTransform],
	);

	const onTouchEnd = useCallback(() => {
		if (!touchStart.current || !touchEnd.current || !isDraggingRef.current) {
			resetPosition();
			return;
		}

		const distanceX = touchStart.current.x - touchEnd.current.x;
		const distanceY = touchStart.current.y - touchEnd.current.y;
		const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
		const finalTranslateX = translateXRef.current;

		if (isHorizontalSwipe && Math.abs(finalTranslateX) > minSwipeDistance) {
			completeSwipe(finalTranslateX < 0 ? 'left' : 'right');
		} else {
			resetPosition();
		}
	}, [minSwipeDistance, completeSwipe, resetPosition]);

	const onTouchCancel = useCallback(() => {
		resetImmediately();
	}, [resetImmediately]);

	useEffect(() => () => {
		resetImmediately();
	}, [resetImmediately]);

	return {
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		onTouchCancel,
	};
};
